/**
 * What the administrator types about a glasses product, and what makes it wrong.
 *
 * Kept as plain data and plain functions so the rules can be tested without a
 * form: every judgement here decides whether a real product goes on a real
 * storefront, and those judgements should not be reachable only by clicking.
 *
 * Everything is held as the text that was typed. Prices become integer minor
 * units through parseMoneyToMinor and stock through parseStock, at the moment
 * they are used — a number kept in state would have to round-trip through a
 * float on every keystroke, and 4.20 does not survive that.
 */

import { SIZE_OPTIONS, type SizeOption } from "../catalog/taxonomy";
import { parseMoneyToMinor } from "../products/money";

/**
 * The merchant code rules, transcribed from the backend that enforces them.
 *
 * app/services/product_domain.py: PRODUCT_CODE_MAX_LENGTH and
 * _PRODUCT_CODE_ALLOWED. They are repeated here for one reason — the backend
 * only sees the code after the photographs have been uploaded and processed, so
 * a code it will refuse must be caught before that work is done rather than
 * after it. The backend remains the authority and validates again.
 *
 * Uniqueness is deliberately not checked here. Only the server can answer it,
 * and it answers with product_code_taken at creation.
 */
export const PRODUCT_CODE_MAX_LENGTH = 64;
const PRODUCT_CODE_ALLOWED = /^[A-Za-z0-9][A-Za-z0-9._\-/]*$/;

/** Collapse whitespace exactly as clean_product_code does before validating. */
export function cleanProductCode(value: string): string {
  return value.trim().split(/\s+/).filter(Boolean).join(" ");
}

export interface PricingDraft {
  /** The administrator's own switch. Nothing infers a sale from the numbers. */
  onSale: boolean;
  /** Used when the product is not on sale. */
  regular: string;
  /** Used when it is. The discount is derived from these two, never entered. */
  original: string;
  sale: string;
}

export interface GlassesDetails {
  name: string;
  productCode: string;
  description: string;
  /**
   * The ONE size this entry is.
   *
   * A frame the shop sells in three sizes is three entries — three codes,
   * three stock positions, three prices — that share one model, and the
   * customer sees one size on the product page. This used to be a LIST, which
   * is how a single entry came to say "Small · Medium" to a shopper.
   *
   * Null until the operator chooses; validation requires it.
   */
  size: SizeOption | null;
  /** Colour folder name -> stock as typed. Blank is not zero; see parseStock. */
  stock: Record<string, string>;
  pricing: PricingDraft;
}

/**
 * A starting point for a new product.
 *
 * The root folder name is offered as the name because retyping it is pointless
 * work, but it is only ever a suggestion in an editable box — a folder named
 * "Aviator Glasses 232" is a photographer's filing decision, not a shop's
 * product name, and it must never be saved without the operator seeing it.
 */
export function emptyDetails(nameSuggestion: string): GlassesDetails {
  return {
    name: nameSuggestion.trim(),
    productCode: "",
    description: "",
    size: null,
    stock: {},
    pricing: { onSale: false, regular: "", original: "", sale: "" },
  };
}

/**
 * Stock as a non-negative whole number, or null when the text is not one.
 *
 * Blank returns null rather than 0. "Out of stock" and "I have not said yet"
 * are different answers, and defaulting the second to the first would publish a
 * product claiming a stock level nobody entered.
 */
export function parseStock(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number.parseInt(trimmed, 10);
  return Number.isSafeInteger(value) ? value : null;
}

export interface Prices {
  /** base_price in minor units — what the contract calls the regular price. */
  base: number | null;
  /** sale_price in minor units, or null when the product is not on sale. */
  sale: number | null;
}

/**
 * The two numbers the product contract stores.
 *
 * There is deliberately no third field. A discount percentage is a view of
 * these two, so storing it would create a second discount model that could
 * disagree with the prices it claims to describe.
 */
export function pricesFor(pricing: PricingDraft): Prices {
  if (!pricing.onSale) {
    return { base: parseMoneyToMinor(pricing.regular), sale: null };
  }
  return {
    base: parseMoneyToMinor(pricing.original),
    sale: parseMoneyToMinor(pricing.sale),
  };
}

/**
 * The saving, as a whole percent, for display only.
 *
 * RE-EXPORTED, not implemented here. This used to be a second implementation —
 * `Math.round` over a float ratio — and it disagreed with the phone, which
 * floors integer minor units: 3000 and 1900 read as 37% on this form and 36% on
 * the shopper's badge. The number an administrator sets a price by has to be
 * the number the shopper is shown, so there is now one Admin implementation and
 * it lives beside the money parsing it belongs with.
 *
 * Still returns null when the pair cannot express a saving, so the form shows
 * nothing rather than a percentage computed from numbers it has already
 * rejected.
 */
export { discountPercent } from "../products/money";

export interface DetailProblem {
  /** Which input this belongs beside. */
  field: string;
  message: string;
  /** Set when the problem is about one colour's stock box. */
  colorFolder?: string;
}

/**
 * Everything wrong with these details, all at once.
 *
 * Reporting only the first fault would make an administrator discover the next
 * one after each correction. Nothing here is a warning: every entry is a reason
 * the product cannot be imported yet.
 */
export function detailProblems(
  details: GlassesDetails,
  /** The colour folders the parser found, which are what need stock. */
  colorFolders: readonly string[],
): DetailProblem[] {
  const problems: DetailProblem[] = [];

  if (!details.name.trim()) {
    problems.push({ field: "name", message: "Give the product a name." });
  }

  problems.push(...productCodeProblems(details.productCode));

  if (!details.description.trim()) {
    problems.push({
      field: "description",
      message: "Write a short description. Customers see this on the product page.",
    });
  }

  if (details.size === null) {
    problems.push({
      field: "size",
      message: "Choose the size this frame is. Another size is a separate product.",
    });
  } else if (!(SIZE_OPTIONS as readonly string[]).includes(details.size)) {
    problems.push({
      field: "size",
      message: `"${details.size}" is not a size this shop sells.`,
    });
  }

  for (const folder of colorFolders) {
    const typed = details.stock[folder] ?? "";
    if (!typed.trim()) {
      problems.push({
        field: "stock",
        colorFolder: folder,
        message: `Enter how many of the ${folder} frames you have. Enter 0 if none.`,
      });
      continue;
    }
    if (parseStock(typed) === null) {
      problems.push({
        field: "stock",
        colorFolder: folder,
        message: `Stock for ${folder} must be a whole number, 0 or more.`,
      });
    }
  }

  problems.push(...pricingProblems(details.pricing));
  return problems;
}

/**
 * The merchant code rules on their own.
 *
 * Each answer names the actual fault. "That code is not valid" would leave an
 * administrator guessing between a stray space, a stray symbol and a code that
 * is simply too long, and the three are fixed differently.
 */
export function productCodeProblems(typed: string): DetailProblem[] {
  const cleaned = cleanProductCode(typed);

  if (!cleaned) {
    return [
      {
        field: "productCode",
        message:
          "Enter the product code your shop uses for this frame. It is how you " +
          "will find the product again.",
      },
    ];
  }
  if (cleaned.length > PRODUCT_CODE_MAX_LENGTH) {
    return [
      {
        field: "productCode",
        message:
          `A product code may be at most ${PRODUCT_CODE_MAX_LENGTH} characters. ` +
          `This one is ${cleaned.length}.`,
      },
    ];
  }
  if (!PRODUCT_CODE_ALLOWED.test(cleaned)) {
    return [
      {
        field: "productCode",
        message:
          "A product code may use letters, digits and - . _ / and must start " +
          "with a letter or digit. Spaces are not allowed.",
      },
    ];
  }
  return [];
}

/** The price rules on their own, so the pricing box can show them as it is used. */
export function pricingProblems(pricing: PricingDraft): DetailProblem[] {
  const problems: DetailProblem[] = [];
  const { base, sale } = pricesFor(pricing);

  if (!pricing.onSale) {
    if (base === null) {
      problems.push({ field: "regular", message: "Enter the price, for example 450.00." });
    } else if (base <= 0) {
      problems.push({ field: "regular", message: "The price must be more than zero." });
    }
    return problems;
  }

  if (base === null) {
    problems.push({
      field: "original",
      message: "Enter the original price, for example 450.00.",
    });
  } else if (base <= 0) {
    problems.push({ field: "original", message: "The original price must be more than zero." });
  }

  if (sale === null) {
    problems.push({ field: "sale", message: "Enter the sale price, for example 360.00." });
  } else if (sale <= 0) {
    problems.push({ field: "sale", message: "The sale price must be more than zero." });
  }

  if (base !== null && sale !== null && base > 0 && sale > 0 && sale >= base) {
    problems.push({
      field: "sale",
      message: "The sale price must be lower than the original price.",
    });
  }

  return problems;
}

/** Are these details complete enough to import? */
export function detailsComplete(
  details: GlassesDetails,
  colorFolders: readonly string[],
): boolean {
  return detailProblems(details, colorFolders).length === 0;
}
